// src/components/admin/TenantSwitcher.js
//
// Lists every tenant and lets the owner-admin jump into any of them ("view as").
// Rendered inside AdminApprovalDashboard. Reads the authoritative `tenants`
// collection; if security rules block that read, it surfaces a clear message.

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAdminView } from '../../contexts/AdminViewContext';
import { makeViewAsUser } from '../../utils/impersonation';

const roleText = (u) => (Array.isArray(u.role) ? u.role : [u.role].filter(Boolean)).join(', ') || 'no role';

export default function TenantSwitcher() {
  const [tenants, setTenants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  // "View as a person": which tenant's people list is open, and its people.
  const [peopleFor, setPeopleFor] = useState(null);
  const [people, setPeople] = useState([]);
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [peopleSearch, setPeopleSearch] = useState('');

  const { impersonatedTenant, setImpersonatedTenant } = useAdminView();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const loadTenants = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Ordered by companyName for a stable, scannable list.
        const q = query(collection(db, 'tenants'), orderBy('companyName'));
        const snap = await getDocs(q);
        if (cancelled) return;
        setTenants(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to load tenants:', e);
        setError(
          e?.code === 'permission-denied'
            ? 'permission-denied'
            : (e?.message || 'Unknown error loading tenants.')
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadTenants();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return tenants;
    return tenants.filter((t) => {
      const name = (t.companyName || '').toLowerCase();
      const ownerEmail = (t.owner?.email || '').toLowerCase();
      const id = (t.id || '').toLowerCase();
      return name.includes(term) || ownerEmail.includes(term) || id.includes(term);
    });
  }, [tenants, search]);

  const handleView = (tenant) => {
    setImpersonatedTenant({ id: tenant.id, companyName: tenant.companyName || tenant.id });
    navigate('/dashboard');
  };

  const togglePeople = async (tenant) => {
    if (peopleFor === tenant.id) { setPeopleFor(null); return; }
    setPeopleFor(tenant.id);
    setPeople([]);
    setPeopleSearch('');
    setPeopleLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'users'), where('tenantId', '==', tenant.id)));
      setPeople(snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(u => u.active !== false)
        .sort((a, b) => String(a.name || a.email).localeCompare(String(b.name || b.email))));
    } catch (e) {
      console.error('Failed to load people:', e);
    } finally {
      setPeopleLoading(false);
    }
  };

  // See the app exactly as this person does. A view, not their login — see
  // withViewAsUser in utils/impersonation.
  const handleViewAs = (tenant, person) => {
    setImpersonatedTenant({
      id: tenant.id,
      companyName: tenant.companyName || tenant.id,
      asUser: makeViewAsUser(person.id, person),
    });
    navigate('/current-loads');
  };

  const billingLabel = (tenant) => {
    const status = tenant.billing?.status || tenant.subscription?.status;
    if (!status) return null;
    const colors = {
      active: 'bg-green-100 text-green-700',
      trial: 'bg-blue-100 text-blue-700',
    };
    return (
      <span className={`px-2 py-0.5 text-xs rounded-full ${colors[status] || 'bg-gray-100 text-gray-600'}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">🔁 View a Tenant's Account</h3>
          <p className="text-sm text-gray-600 mt-1">
            Open any tenant to see their loads, drivers, accounting and everything else — for support.
            A banner stays on screen the whole time so you always know you're in owner view.
          </p>
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search company, email, or ID…"
          className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {impersonatedTenant?.id && (
        <div className="mb-4 text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
          Currently viewing <strong>{impersonatedTenant.companyName}</strong>. Selecting another tenant switches your view.
        </div>
      )}

      {isLoading && (
        <div className="text-center py-10 text-gray-500">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
          Loading tenants…
        </div>
      )}

      {!isLoading && error === 'permission-denied' && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <p className="font-semibold mb-1">Cannot read the tenants list.</p>
          <p>Your Firestore security rules don't allow this account to read the <code>tenants</code> collection.
          Add an owner-admin read rule in the Firebase Console, then reload.</p>
        </div>
      )}

      {!isLoading && error && error !== 'permission-denied' && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          Failed to load tenants: {error}
        </div>
      )}

      {!isLoading && !error && filtered.length === 0 && (
        <div className="text-center py-10 text-gray-500">
          {tenants.length === 0 ? 'No tenants found.' : 'No tenants match your search.'}
        </div>
      )}

      {!isLoading && !error && filtered.length > 0 && (
        <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
          {filtered.map((tenant) => (
            <React.Fragment key={tenant.id}>
            <div
              className="flex items-center justify-between gap-4 p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {tenant.companyName || tenant.id}
                  </p>
                  {billingLabel(tenant)}
                </div>
                <p className="text-xs text-gray-500 truncate">
                  {tenant.owner?.email || 'No owner email'} · <span className="font-mono">{tenant.id}</span>
                </p>
              </div>
              <div className="shrink-0 flex gap-2">
                <button
                  onClick={() => togglePeople(tenant)}
                  className="bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg transition-colors"
                >
                  {peopleFor === tenant.id ? 'Close' : 'View as a person…'}
                </button>
                <button
                  onClick={() => handleView(tenant)}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                >
                  View as this company →
                </button>
              </div>
            </div>
            {peopleFor === tenant.id && (
              <div className="px-4 pb-4 bg-gray-50">
                <input
                  type="text"
                  value={peopleSearch}
                  onChange={(e) => setPeopleSearch(e.target.value)}
                  placeholder="Search name, email or role…"
                  className="w-full sm:w-72 mt-2 mb-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
                />
                {peopleLoading && <p className="text-sm text-gray-500 py-2">Loading people…</p>}
                {!peopleLoading && people.length === 0 && <p className="text-sm text-gray-500 py-2">No active people found.</p>}
                <div className="max-h-80 overflow-y-auto divide-y divide-gray-200 border border-gray-200 rounded-lg bg-white">
                  {people
                    .filter(p => {
                      const term = peopleSearch.trim().toLowerCase();
                      if (!term) return true;
                      return [p.name, p.email, roleText(p)].some(x => String(x || '').toLowerCase().includes(term));
                    })
                    .map(p => (
                      <div key={p.id} className="flex items-center justify-between gap-3 px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{p.name || p.email}</p>
                          <p className="text-xs text-gray-500 truncate">{roleText(p)} · {p.email}</p>
                        </div>
                        <button
                          onClick={() => handleViewAs(tenant, p)}
                          className="shrink-0 bg-amber-500 hover:bg-amber-600 text-amber-950 text-xs font-semibold px-3 py-1.5 rounded-md"
                        >
                          View as {String(p.name || p.email).split(' ')[0]} →
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
